import { rootApi } from './rootApi';

rootApi.enhanceEndpoints({
  addTagTypes: [
    'BuyCampaignsStats',
    'BuyLeadsCampaign',
    'BuyerDayparting',
    'HasBuyLeadsCampaigns',
  ],
});

export const buyLeadsCampaignApi = rootApi.injectEndpoints({
  endpoints(build) {
    return {
      /**
       * Campaign
       */
      getHasBuyLeadsCampaigns: build.query({
        query: (params) => ({
          url: '/LeadBuyCampaign/',
          method: 'get',
          params: {
            page_size: 1,
            archive_mode: 'all',
            ...params,
          },
        }),
        keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
        providesTags: () => {
          return ['HasBuyLeadsCampaigns'];
        },
        transformResponse: ({ count } = {}) => {
          return !!count;
        },
      }),

      getBuyLeadsCampaigns: build.query({
        query: ({ ...params } = {}) => ({
          url: '/LeadBuyCampaign/',
          method: 'get',
          params,
        }),
        providesTags: () => {
          return ['BuyLeadsCampaign'];
        },
      }),
      getBuyLeadsCampaign: build.query({
        query: ({ id, ...params } = {}) => ({
          url: `/LeadBuyCampaign/${id}/`,
          method: 'get',
          params: { archive_mode: 'all', ...params },
        }),
        providesTags: (props) => {
          const { id } = props || {};

          return [{ type: 'BuyLeadsCampaign', id }];
        },
        transformResponse: (response) => {
          if (!response) return response;

          const res = { ...response };
          res.vertical = res.taxonomy.vertical;

          return res;
        },
      }),
      createBuyLeadsCampaign: build.mutation({
        query: ({ ...data } = {}) => ({
          url: '/LeadBuyCampaign/',
          method: 'post',
          data,
        }),
        invalidatesTags: () => {
          return [
            { type: 'BuyCampaignsStats' },
            { type: 'BuyLeadsCampaign' },
            { type: 'BuyCampaignsGroupsStats' },
            { type: 'HasBuyLeadsCampaigns' },
          ];
        },
      }),

      patchBuyLeadsCampaign: build.mutation({
        query: ({ id, archive_mode = 'all', ...data } = {}) => ({
          url: `/LeadBuyCampaign/${id}/`,
          method: 'patch',
          data,
          params: { archive_mode },
        }),
        invalidatesTags: ({ id } = {}) => {
          return [{ type: 'BuyLeadsCampaign', id }];
        },
      }),
      deleteBuyLeadsCampaigns: build.mutation({
        queryFn: async (queryArgs, _queryApi, _extraOptions, fetchWithBQ) => {
          const { ids, ...params } = queryArgs;
          try {
            const promiseResult = await Promise.all(
              ids.map((id) =>
                fetchWithBQ({
                  url: `/LeadBuyCampaign/${id}/`,
                  method: 'delete',
                  params,
                }),
              ),
            );
            return {
              data: promiseResult,
            };
          } catch (error) {
            return { error };
          }
        },
        invalidatesTags: () => {
          return [
            'BuyCampaignsStats',
            'BuyCampaignsGroupsStats',
            'HasBuyLeadsCampaigns',
          ];
        },
      }),
      archiveBuyLeadsCampaigns: build.mutation({
        queryFn: async (queryArgs, _queryApi, _extraOptions, fetchWithBQ) => {
          const { ids } = queryArgs;
          try {
            const promiseResult = await Promise.all(
              ids.map((id) =>
                fetchWithBQ({
                  url: `/LeadBuyCampaign/${id}/archive/`,
                  method: 'post',
                }),
              ),
            );
            return {
              data: promiseResult,
            };
          } catch (error) {
            return { error };
          }
        },
        invalidatesTags: () => {
          return [
            'BuyLeadsCampaign',
            'BuyCampaignsStats',
            'BuyCampaignsGroupsStats',
            'HasBuyLeadsCampaigns',
          ];
        },
      }),
      unarchiveBuyLeadsCampaigns: build.mutation({
        queryFn: async (queryArgs, _queryApi, _extraOptions, fetchWithBQ) => {
          const { ids } = queryArgs;
          try {
            const promiseResult = await Promise.all(
              ids.map((id) =>
                fetchWithBQ({
                  url: `/LeadBuyCampaign/${id}/unarchive/`,
                  method: 'post',
                }),
              ),
            );
            return {
              data: promiseResult,
            };
          } catch (error) {
            return { error };
          }
        },
        invalidatesTags: () => {
          return [
            'BuyLeadsCampaign',
            'BuyCampaignsStats',
            'BuyCampaignsGroupsStats',
            'HasBuyLeadsCampaigns',
          ];
        },
      }),
      changeStateBuyLeadsCampaigns: build.mutation({
        queryFn: async (queryArgs, _queryApi, _extraOptions, fetchWithBQ) => {
          const { ids, status } = queryArgs;
          try {
            const promiseResult = await Promise.all(
              ids.map((id) =>
                fetchWithBQ({
                  url: `/LeadBuyCampaign/${id}/`,
                  method: 'patch',
                  data: { status },
                }),
              ),
            );
            return {
              data: promiseResult,
            };
          } catch (error) {
            return { error };
          }
        },
        invalidatesTags: () => {
          return ['BuyLeadsCampaign', 'BuyCampaignsStats'];
        },
      }),

      /**
       * Stats
       */
      getBuyLeadsCampaignsStats: build.query({
        query: (params = {}) => ({
          url: '/stats/buy_campaign/',
          method: 'get',
          params: {
            product: 'lead',
            ordering: '-pk',
            archive_mode: 'all',
            ...params,
          },
        }),
        providesTags: (result) => {
          return !!result?.results?.length
            ? result.results.map(({ buyCampaignId }) => ({
                type: 'BuyCampaignsStats',
                id: buyCampaignId,
              }))
            : ['BuyCampaignsStats'];
        },
      }),
      getBuyerDayparting: build.query({
        query: () => ({
          url: '/BuyerDayparting/',
          method: 'get',
        }),
        providesTags: () => {
          return ['BuyerDayparting'];
        },
      }),
      createBuyerDayparting: build.mutation({
        query: ({ ...data } = {}) => ({
          url: '/BuyerDayparting/',
          method: 'post',
          data,
        }),
        invalidatesTags: () => {
          return ['BuyerDayparting'];
        },
      }),
    };
  },
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints

export const {
  useGetHasBuyLeadsCampaignsQuery,
  useLazyGetBuyLeadsCampaignsStatsQuery,
  useGetBuyLeadsCampaignsQuery,
  useGetBuyLeadsCampaignQuery,
  useCreateBuyLeadsCampaignMutation,
  usePatchBuyLeadsCampaignMutation,
  useGetBuyLeadsCampaignsStatsQuery,
  useDeleteBuyLeadsCampaignsMutation,
  useArchiveBuyLeadsCampaignsMutation,
  useUnarchiveBuyLeadsCampaignsMutation,
  useChangeStateBuyLeadsCampaignsMutation,
  useGetBuyerDaypartingQuery,
} = buyLeadsCampaignApi;
